/**
 * #2205 — classify a scanned value against the known target (the printed
 * label value the rep typed during calibration). Turns raw decoder output
 * into the failure taxonomy RMR observed on real shelf tags, so the
 * diagnostics aid can aggregate "how is it wrong" across repeated scans.
 *
 * Pure + framework-free → trivially unit-tested.
 */

export type ScanDiffKind =
  | 'exact' // decoded === target
  | 'no-read' // nothing decoded
  | 'leading-zero-missing' // decoded is a suffix of target, dropped prefix is all zeros
  | 'leading-missing' // decoded is a suffix of target, dropped prefix has non-zero digits
  | 'truncated' // decoded is a prefix of target — trailing digits cut off
  | 'extra' // decoded contains target plus extra leading/trailing chars
  | 'mismatch'; // none of the above — a "way off" read

export interface ScanDiff {
  kind: ScanDiffKind;
  /** Digits present in target but absent from this read (for leading/truncated). */
  missing?: string;
  /** Extra digits the read added (for 'extra'). */
  extra?: string;
}

const digitsOnly = (s: string | null | undefined): string => (s ?? '').replace(/\D/g, '');

export function classifyScan(target: string, decoded: string | null | undefined): ScanDiff {
  const t = digitsOnly(target);
  const d = digitsOnly(decoded);

  if (d.length === 0) return { kind: 'no-read' };
  if (d === t) return { kind: 'exact' };

  // decoded is a SUFFIX of target → leading digits were dropped.
  if (t.length > d.length && t.endsWith(d)) {
    const missing = t.slice(0, t.length - d.length);
    return { kind: /^0+$/.test(missing) ? 'leading-zero-missing' : 'leading-missing', missing };
  }

  // decoded is a PREFIX of target → trailing digits were cut off (truncated).
  if (t.length > d.length && t.startsWith(d)) {
    return { kind: 'truncated', missing: t.slice(d.length) };
  }

  // decoded CONTAINS the full target plus extra chars on one side.
  if (d.length > t.length && (d.startsWith(t) || d.endsWith(t))) {
    const extra = d.startsWith(t) ? d.slice(t.length) : d.slice(0, d.length - t.length);
    return { kind: 'extra', extra };
  }

  return { kind: 'mismatch' };
}

/** Aggregate a batch of diffs into a per-kind tally + a one-line verdict. */
export function summarizeScans(diffs: ScanDiff[]): {
  total: number;
  counts: Record<ScanDiffKind, number>;
  verdict: string;
} {
  const counts: Record<ScanDiffKind, number> = {
    exact: 0,
    'no-read': 0,
    'leading-zero-missing': 0,
    'leading-missing': 0,
    truncated: 0,
    extra: 0,
    mismatch: 0,
  };
  for (const d of diffs) counts[d.kind] += 1;
  const total = diffs.length;
  const parts: string[] = [`${counts.exact}/${total} exact`];
  if (counts['leading-zero-missing']) parts.push(`${counts['leading-zero-missing']} dropped leading 0`);
  if (counts['leading-missing']) parts.push(`${counts['leading-missing']} dropped leading digit`);
  if (counts.truncated) parts.push(`${counts.truncated} truncated`);
  if (counts.extra) parts.push(`${counts.extra} extra`);
  if (counts.mismatch) parts.push(`${counts.mismatch} off`);
  if (counts['no-read']) parts.push(`${counts['no-read']} no-read`);
  return { total, counts, verdict: parts.join(' · ') };
}
